// frontend/src/modules/payroll/pages/PayrollReportsPage.tsx
//
// Payroll reports — `/dashboard/payroll/reports`.
//
// Aggregates real payslip data into:
//   * KPI strip (this month, employees, avg/employee, pending)
//   * "Total spend this month / this year" tiles
//   * Per-employee cost summary table
//   * Per-run summary table
//
// The "Export CSV" buttons download server-rendered CSVs
// (GET /api/payroll/reports/employees|runs). They deliberately do NOT build
// the file client-side: a browser-built CSV sits outside every server gate,
// including the trial-export block.
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowUpRight, Banknote, Download, Calendar, BarChart3 } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatMoney } from '../../../design-system/format';
import { VISUAL } from '../../../design-system/visual';
import { payrollApi } from '../api';
import { runStatusView } from '../runStatus';
import { PayrollSummaryStrip } from '../components/PayrollSummaryStrip';
import type { Payslip, PayrollRun } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

type Row = Payslip & { run: PayrollRun };

export function PayrollReportsPage() {
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';

  const [rows, setRows] = React.useState<Row[]>([]);
  const [loading, setLoading] = React.useState(true);
  const empQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    payrollApi.listAllPayslips()
      .then((all) => { if (!cancelled) setRows(all); })
      .catch((err) => {
        if (!cancelled) toast.error('Couldn\'t load report data', { description: err?.message ?? 'unknown' });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const employees = empQ.data?.employees ?? [];
  const employeeById: Record<string, EmployeeWithUser> = {};
  for (const e of employees) employeeById[e.id] = e;

  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  // Big totals.
  const monthSpend = rows.reduce((s, r) => {
    const d = new Date(r.run.period_start);
    return d.getFullYear() === y && d.getMonth() === m ? s + Number(r.net_amount) : s;
  }, 0);
  const yearSpend = rows.reduce((s, r) => {
    const d = new Date(r.run.period_start);
    return d.getFullYear() === y ? s + Number(r.net_amount) : s;
  }, 0);
  const monthCount = rows.filter((r) => {
    const d = new Date(r.run.period_start);
    return d.getFullYear() === y && d.getMonth() === m;
  }).length;
  const yearCount = rows.filter((r) => new Date(r.run.period_start).getFullYear() === y).length;

  // Per-employee aggregation.
  const perEmployee = React.useMemo(() => {
    const groups: Record<string, { payslips: Row[]; gross: number; deductions: number; net: number }> = {};
    for (const r of rows) {
      const g = groups[r.employee_id] ||= { payslips: [], gross: 0, deductions: 0, net: 0 };
      g.payslips.push(r);
      g.gross += Number(r.gross_additions);
      g.deductions += Number(r.gross_deductions);
      g.net += Number(r.net_amount);
    }
    return Object.entries(groups)
      .map(([employeeId, agg]) => ({ employeeId, ...agg }))
      .sort((a, b) => b.net - a.net);
  }, [rows]);

  // Per-run aggregation.
  const perRun = React.useMemo(() => {
    const groups: Record<string, { run: PayrollRun; payslips: Row[]; gross: number; deductions: number; net: number; employees: Set<string> }> = {};
    for (const r of rows) {
      const g = groups[r.run.id] ||= { run: r.run, payslips: [], gross: 0, deductions: 0, net: 0, employees: new Set() };
      g.payslips.push(r);
      g.gross += Number(r.gross_additions);
      g.deductions += Number(r.gross_deductions);
      g.net += Number(r.net_amount);
      g.employees.add(r.employee_id);
    }
    return Object.values(groups).sort((a, b) =>
      b.run.period_end.localeCompare(a.run.period_end),
    );
  }, [rows]);

  // Both CSVs are rendered by the server so they pass through the same
  // export gate as every other download; a blocked export (e.g. a trial
  // tenant's 402) surfaces the backend's message here.
  const [exporting, setExporting] = React.useState<'employees' | 'runs' | null>(null);

  async function runExport(kind: 'employees' | 'runs', download: () => Promise<void>, filename: string) {
    setExporting(kind);
    try {
      await download();
      toast.success('CSV exported', { description: filename });
    } catch (err: any) {
      toast.error('Export failed', { description: err?.message ?? String(err) });
    } finally {
      setExporting(null);
    }
  }

  const exportEmployees = () => runExport(
    'employees', payrollApi.downloadEmployeeCostCsv, 'payroll-employees-all-time.csv',
  );
  const exportRuns = () => runExport(
    'runs', payrollApi.downloadRunSummaryCsv, 'payroll-runs-all-time.csv',
  );

  return (
    <div>
      <PageHeader
        eyebrow="Team / Payroll / Reports"
        title="Payroll reports"
        description="Spend, employee cost, and run-level totals. Numbers come straight from finalized payroll data."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/dashboard/payroll">
              <Button variant="outline" className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
                <Banknote className="h-3.5 w-3.5" /> Runs
              </Button>
            </Link>
            <Link to="/dashboard/payroll/payslips">
              <Button variant="ghost" className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5 text-muted-foreground">
                Payslips <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {/* KPI strip — shared with PayrollPage */}
        <PayrollSummaryStrip rows={rows} loading={loading} />

        {/* This month / this year hero tiles */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 130, damping: 22 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-4"
        >
          <HeroTile
            label="Total this month"
            value={formatMoney(monthSpend, currency)}
            caption={`${monthCount} payslip${monthCount === 1 ? '' : 's'} · ${now.toLocaleString(undefined, { month: 'long', year: 'numeric' })}`}
            icon={<Calendar className="h-4 w-4" />}
          />
          <HeroTile
            label="Total this year"
            value={formatMoney(yearSpend, currency)}
            caption={`${yearCount} payslip${yearCount === 1 ? '' : 's'} · ${y}`}
            icon={<BarChart3 className="h-4 w-4" />}
            accent
          />
        </motion.section>

        {/* Per employee */}
        <section className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <h3 className={VISUAL.dashSection}>Per-employee cost</h3>
              <p className="text-[12.5px] text-muted-foreground mt-1">Sum of every payslip across every run, top-spender first.</p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={exportEmployees}
              disabled={perEmployee.length === 0 || exporting !== null}
              className="rounded-full h-9 px-4 text-[12.5px] font-semibold gap-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting === 'employees' ? 'Exporting…' : 'Export CSV'}
            </Button>
          </div>
          {loading ? (
            <TableSkeleton />
          ) : perEmployee.length === 0 ? (
            <EmptyHint label="No payroll data yet" />
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  <tr className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground/80">
                    <th className="text-left py-2.5 px-2 font-semibold">Employee</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Payslips</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Gross</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Deductions</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {perEmployee.map((e) => {
                    const emp = employeeById[e.employeeId];
                    const name = emp?.name ?? e.employeeId.slice(0, 8);
                    return (
                      <tr key={e.employeeId} className="border-t border-border/40">
                        <td className="py-3 px-2 align-top">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-9 w-9 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center text-[12px] font-bold text-saffron-foreground shrink-0">
                              {(name?.[0] ?? '?').toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[13.5px] font-semibold text-foreground truncate">{name}</p>
                              <p className="text-[11.5px] text-muted-foreground truncate">{emp?.email ?? '—'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 align-middle text-right text-[13px] font-mono tabular-nums">{e.payslips.length}</td>
                        <td className="py-3 px-2 align-middle text-right text-[13px] font-mono tabular-nums">{formatMoney(e.gross, currency)}</td>
                        <td className="py-3 px-2 align-middle text-right text-[13px] font-mono tabular-nums text-muted-foreground">− {formatMoney(e.deductions, currency)}</td>
                        <td className="py-3 px-2 align-middle text-right text-[14px] font-semibold font-mono tabular-nums">{formatMoney(e.net, currency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Per run */}
        <section className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <h3 className={VISUAL.dashSection}>Per-run summary</h3>
              <p className="text-[12.5px] text-muted-foreground mt-1">One row per payroll cycle. Click through to open the run.</p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={exportRuns}
              disabled={perRun.length === 0 || exporting !== null}
              className="rounded-full h-9 px-4 text-[12.5px] font-semibold gap-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting === 'runs' ? 'Exporting…' : 'Export CSV'}
            </Button>
          </div>
          {loading ? (
            <TableSkeleton />
          ) : perRun.length === 0 ? (
            <EmptyHint label="No runs yet" />
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  <tr className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground/80">
                    <th className="text-left py-2.5 px-2 font-semibold">Period</th>
                    <th className="text-left py-2.5 px-2 font-semibold">Status</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Employees</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Gross</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Deductions</th>
                    <th className="text-right py-2.5 px-2 font-semibold">Net</th>
                    <th className="text-right py-2.5 px-2 font-semibold" />
                  </tr>
                </thead>
                <tbody>
                  {perRun.map((r) => {
                    const status = runStatusView(r.run).label;
                    return (
                      <tr key={r.run.id} className="border-t border-border/40">
                        <td className="py-3 px-2 align-middle text-[13.5px] font-semibold text-foreground">
                          {formatDate(r.run.period_start)} – {formatDate(r.run.period_end)}
                        </td>
                        <td className="py-3 px-2 align-middle text-[12.5px] text-muted-foreground">{status}</td>
                        <td className="py-3 px-2 align-middle text-right text-[13px] font-mono tabular-nums">{r.employees.size}</td>
                        <td className="py-3 px-2 align-middle text-right text-[13px] font-mono tabular-nums">{formatMoney(r.gross, currency)}</td>
                        <td className="py-3 px-2 align-middle text-right text-[13px] font-mono tabular-nums text-muted-foreground">− {formatMoney(r.deductions, currency)}</td>
                        <td className="py-3 px-2 align-middle text-right text-[14px] font-semibold font-mono tabular-nums">{formatMoney(r.net, currency)}</td>
                        <td className="py-3 px-2 align-middle text-right">
                          <Link
                            to={`/dashboard/payroll/runs/${r.run.id}`}
                            className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-foreground hover:text-saffron-foreground transition-colors"
                          >
                            View <ArrowUpRight className="h-3 w-3" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ─────────────────────────── tiles + bits ─────────────────────────── */

function HeroTile({
  label, value, caption, icon, accent = false,
}: { label: string; value: React.ReactNode; caption: string; icon: React.ReactNode; accent?: boolean }) {
  return (
    <div className={[VISUAL.bento, 'p-7'].join(' ')}>
      <div className="flex items-center justify-between mb-3">
        <p className={VISUAL.eyebrow}>{label}</p>
        <span className={[
          'inline-flex h-9 w-9 items-center justify-center rounded-xl ring-1',
          accent ? 'bg-saffron/15 text-saffron-foreground ring-saffron/30' : 'bg-muted text-muted-foreground ring-border/50',
        ].join(' ')}>{icon}</span>
      </div>
      <p className={VISUAL.dashBigNumber}>{value}</p>
      <p className="text-[12.5px] text-muted-foreground mt-3">{caption}</p>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2 pt-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-2 py-3">
          <div className="h-9 w-9 rounded-2xl bg-muted/60 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-muted/60 animate-pulse" />
            <div className="h-2.5 w-1/4 rounded bg-muted/40 animate-pulse" />
          </div>
          <div className="h-3 w-20 rounded bg-muted/60 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function EmptyHint({ label }: { label: string }) {
  return (
    <div className="py-10 text-center">
      <p className="text-[14px] font-semibold text-foreground">{label}</p>
      <p className="text-[12.5px] text-muted-foreground mt-1.5">
        Numbers appear here once you compute and finalize a run.
      </p>
      <div className="mt-5">
        <Link to="/dashboard/payroll">
          <Button variant="outline" className="rounded-full gap-1.5">
            <Banknote className="h-3.5 w-3.5" /> Open payroll
          </Button>
        </Link>
      </div>
    </div>
  );
}
